import { sentryLog } from "../../../tools/sentry";
import { formSet } from "../../gvc/testNewForm/utils";
import { validationFns } from "../../../components/form/validations";
import { VolumeSetInput } from "../../../schema/types/volumeSet/input";
import { parseLink } from "../../../utils/linkParser/linkParser";

export function getValidator(id: string) {
  return async function validator(v: VolumeSetInput): Promise<boolean | null> {
    // setup
    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    try {
      const fs = validationFns("Name");
      for (const f of [fs.required, fs.name]) {
        const r = f(v.name);
        if (r !== true) {
          setError(`name`, r);
        }
      }

      if (v.description.length > 250) {
        setError(`description`, `Description can be at most 250 characters`);
      }

      if (!v.gvc?.link) {
        setError(`gvc`, "GVC is required");
      } else {
        const parsedLink = parseLink(v.gvc.link, { useInputCtx: true });
        if (!parsedLink.isValid) {
          setError(`gvc`, parsedLink.errorMessage);
        }
      }

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }

      if (v.tags.length > 50) {
        setError(`tagsroot`, "Tags cannot be more than 50 items");
      }
      // tags end

      let x = v.initialCapacity;
      let xn = Number(x);
      if (v.initialCapacity.length < 1) {
        setError(`initialCapacity`, "Initial Capacity is required");
      } else {
        if (xn < 10) {
          setError(`initialCapacity`, "Initial Capacity must be at least 10 GB");
        }
      }

      let regex: RegExp;
      if (v.storageClassSuffix.length > 0) {
        regex = /^[a-zA-Z][0-9a-zA-Z\-_]*$/;
        if (!regex.test(v.storageClassSuffix)) {
          setError(`storageClassSuffix`, `Storage Class Suffix must be a valid match for regex: ${regex}`);
        }
      }

      if (v.snapshots.isEnabled) {
        if (v.snapshots.schedule.length < 1) {
          setError(`snapshots.schedule`, "Schedule is required");
        }
      }

      if (v.autoscaling.isEnabled) {
        if (
          v.autoscaling.maxCapacity.length < 1 &&
          v.autoscaling.minFreePercentage.length < 1 &&
          v.autoscaling.scalingFactor.length < 1
        ) {
          setError(`autoscalingroot`, "At least one property is required to enable autoscaling");
        }

        if (v.autoscaling.maxCapacity.length > 0) {
          xn = Number(v.autoscaling.maxCapacity);
          if (xn < 10) {
            setError(`autoscaling.maxCapacity`, "Max Capacity must be at least 10 GB");
          }
        }

        if (v.autoscaling.minFreePercentage.length > 0) {
          xn = Number(v.autoscaling.minFreePercentage);
          if (xn < 1) {
            setError(`autoscaling.minFreePercentage`, "Min Free Percentage must be at least 1");
          }
          if (xn > 100) {
            setError(`autoscaling.minFreePercentage`, "Min Free Percentage can be at most 100");
          }
        }

        if (v.autoscaling.scalingFactor.length > 0) {
          xn = Number(v.autoscaling.scalingFactor);
          if (xn < 1.1) {
            setError(`autoscaling.scalingFactor`, "Scaling Factor must be at least 1.1");
          }
        }

        if (v.autoscaling.predictive.isEnabled) {
          const p = v.autoscaling.predictive;
          if (p.lookbackHours && (Number(p.lookbackHours) < 1 || Number(p.lookbackHours) > 168)) {
            setError("autoscaling.predictive.lookbackHours", "Lookback Hours must be between 1 and 168");
          }
          if (p.projectionHours && (Number(p.projectionHours) < 1 || Number(p.projectionHours) > 72)) {
            setError("autoscaling.predictive.projectionHours", "Projection Hours must be between 1 and 72");
          }
          if (p.minDataPoints && (Number(p.minDataPoints) < 2 || Number(p.minDataPoints) > 100)) {
            setError("autoscaling.predictive.minDataPoints", "Min Data Points must be between 2 and 100");
          }
          if (p.minGrowthRateGBPerHour && Number(p.minGrowthRateGBPerHour) < 0) {
            setError("autoscaling.predictive.minGrowthRateGBPerHour", "Min Growth Rate must be at least 0");
          }
          if (p.scalingFactor && Number(p.scalingFactor) < 1.1) {
            setError("autoscaling.predictive.scalingFactor", "Scaling Factor must be at least 1.1");
          }
        }
      }

      // custom encyrption start
      for (const customEncryptionIndexStr in v.customEncryption) {
        const customEncryptionIndex = Number(customEncryptionIndexStr);
        const customEncryption = v.customEncryption[customEncryptionIndex];
        if (!customEncryption.name) {
          setError(`customEncryption.${customEncryptionIndex}.name`, "Region is required");
          setError(`customEncryptionroot`, "Regions are required");
        }
        if (!customEncryption.value) {
          setError(`customEncryption.${customEncryptionIndex}.value`, "Key ID is required");
          setError(`customEncryptionroot`, "Key IDs are required");
        }
      }
      // custom encyrption end

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      sentryLog("volumeset validator error", { "data.error": e.message });
      formSet(id, `errors`, { root: { message: "An unexpected error occurred. This has been logged and we will fix it." } });
      return true;
    }
  };
}
